import { useId } from 'react';
import { Check } from 'lucide-react';

export interface OpcionSegmentada<T extends string> {
  valor: T;
  etiqueta: string;
  /**
   * Nombre completo para lectores de pantalla cuando la etiqueta visible es
   * corta ("1 m" → "1 mes"). Empieza con la etiqueta visible (WCAG 2.5.3).
   */
  nombre?: string;
}

interface Props<T extends string> {
  /** Nombre del grupo para lectores de pantalla, por ejemplo "Unidad de peso". */
  etiqueta: string;
  /** true = la etiqueta se muestra arriba; false = solo para lectores de pantalla. */
  etiquetaVisible?: boolean;
  valor: T;
  opciones: readonly OpcionSegmentada<T>[];
  onCambio: (valor: T) => void;
  /** Ocupa todo el ancho, con las opciones repartidas. */
  completo?: boolean;
  /**
   * Para cinco opciones cortas a 320 px, como los rangos de una gráfica: sin
   * palomita y con menos margen. La elegida se sigue distinguiendo por el
   * fondo, la sombra y el peso de letra, y `aria-pressed` la anuncia.
   */
  compacto?: boolean;
}

/**
 * Selector de pocas opciones excluyentes, como kg | lb o Sistema | Claro | Oscuro,
 * con el estilo de opciones de Foco.
 *
 * Son botones con `aria-pressed` dentro de un grupo con nombre: cada opción
 * mide al menos 44 puntos (Req. 8.1) y la elegida se distingue con el fondo,
 * el peso de letra y una palomita, no solo con color (Req. 8.5).
 */
export function Segmentado<T extends string>({
  etiqueta,
  etiquetaVisible = false,
  valor,
  opciones,
  onCambio,
  completo = false,
  compacto = false,
}: Props<T>) {
  const id = useId();

  return (
    <div className={completo ? 'space-y-[7px]' : 'inline-block space-y-[7px]'}>
      <span id={id} className={etiquetaVisible ? 'block text-[13px] text-texto-suave' : 'sr-only'}>
        {etiqueta}
      </span>
      <div
        role="group"
        aria-labelledby={id}
        className={`${completo ? 'flex w-full' : 'inline-flex'} gap-[3px] rounded-[15px] bg-superficie-suave p-1`}
      >
        {opciones.map((opcion) => {
          const elegida = opcion.valor === valor;
          return (
            <button
              key={opcion.valor}
              type="button"
              aria-pressed={elegida}
              aria-label={opcion.nombre}
              onClick={() => onCambio(opcion.valor)}
              className={[
                'inline-flex min-h-11 items-center justify-center gap-1 rounded-xl transition-colors',
                compacto ? 'min-w-11 px-2 text-[13px]' : 'min-w-12 px-3 text-[14px]',
                completo ? 'flex-1' : '',
                elegida
                  ? 'bg-superficie font-medium text-texto shadow-[0_1px_3px_var(--sombra)]'
                  : 'text-texto-suave',
              ].join(' ')}
            >
              {elegida && !compacto && <Check className="size-4 text-marca" />}
              {opcion.etiqueta}
            </button>
          );
        })}
      </div>
    </div>
  );
}
